/**
 * HDT Page Loader
 * Beautiful loading screen with logo animation
 *
 * ⚡ NETWORK-OPTIMIZED VERSION
 * - No artificial timeout
 * - Waits for window 'load' event (all resources)
 * - Smart image checker tracks loading progress
 * - PerformanceObserver for enhanced accuracy
 * - Automatically hides when page is fully ready
 */

class HDTPageLoader {
  constructor(options = {}) {
    this.options = {
      fadeOutDuration: options.fadeOutDuration || 500,
      showLoader: options.showLoader !== false,
      logoAnimation: options.logoAnimation || "pulse",
      loaderStyle: options.loaderStyle || "spinner",
      checkInterval: options.checkInterval || 100, // Check images every 100ms
    };

    this.loader = null;
    this.isLoaded = false;

    if (this.options.showLoader) {
      this.init();
    }
  }

  init() {
    // Create loader if it doesn't exist
    if (!document.querySelector(".hdt-page-loader")) {
      this.createLoader();
    } else {
      this.loader = document.querySelector(".hdt-page-loader");
    }

    // Add loading class to body and html
    document.body.classList.add("hdt-loading");
    document.documentElement.classList.add("hdt-loading");

    // Listen to page load events
    this.setupEventListeners();
  }

  createLoader() {
    this.loader = document.createElement("div");
    this.loader.className = "hdt-page-loader";
    this.loader.innerHTML = this.getLoaderHTML();

    // Insert at the beginning of body
    document.body.insertBefore(this.loader, document.body.firstChild);
  }

  getLoaderHTML() {
    const logoURL = this.getLogoURL();
    const animationClass = this.getAnimationClass();
    const loaderElement = this.getLoaderElement();

    return `
      <div class="hdt-loader-content">
        ${
          this.options.loaderStyle === "particles"
            ? this.getParticlesHTML()
            : ""
        }
        
        <div class="hdt-loader-logo-wrapper" style="position: relative;">
          <img 
            src="${logoURL}" 
            alt="Loading..." 
            class="hdt-loader-logo ${animationClass}"
          >
        </div>
        
        ${loaderElement}
        
        <div class="hdt-loader-text">
          Loading...
        </div>
      </div>
    `;
  }

  getLogoURL() {
    // Try to get logo from meta tag or settings
    const logoMeta = document.querySelector('meta[name="logo-url"]');
    if (logoMeta) {
      return logoMeta.content;
    }

    // Default logo path
    return "/files/Logo%20Solux_Plan%20de%20travail%201.png";
  }

  getAnimationClass() {
    switch (this.options.logoAnimation) {
      case "bounce":
        return "hdt-bounce";
      case "rotate":
        return "hdt-rotate";
      case "pulse":
      default:
        return "";
    }
  }

  getLoaderElement() {
    switch (this.options.loaderStyle) {
      case "dots":
        return `
          <div class="hdt-loader-dots">
            <div class="hdt-loader-dot"></div>
            <div class="hdt-loader-dot"></div>
            <div class="hdt-loader-dot"></div>
          </div>
        `;

      case "progress":
        return `
          <div class="hdt-loader-progress">
            <div class="hdt-loader-progress-bar"></div>
          </div>
        `;

      case "circle":
        return `
          <div class="hdt-loader-circle">
            <svg viewBox="0 0 100 100">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style="stop-color:#F7931E;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#FDB64E;stop-opacity:1" />
                </linearGradient>
              </defs>
              <circle class="hdt-loader-circle-bg" cx="50" cy="50" r="45"></circle>
              <circle class="hdt-loader-circle-progress" cx="50" cy="50" r="45"></circle>
            </svg>
          </div>
        `;

      case "spinner":
      default:
        return '<div class="hdt-loader-spinner"></div>';
    }
  }

  getParticlesHTML() {
    let particlesHTML = '<div class="hdt-loader-particles">';
    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      const left = Math.random() * 100;
      const delay = Math.random() * 6;
      const duration = 4 + Math.random() * 4;

      particlesHTML += `
        <div 
          class="hdt-loader-particle" 
          style="
            left: ${left}%;
            animation-delay: ${delay}s;
            animation-duration: ${duration}s;
          "
        ></div>
      `;
    }

    particlesHTML += "</div>";
    return particlesHTML;
  }

  setupEventListeners() {
    // ONLY hide when page is FULLY loaded
    if (document.readyState === "complete") {
      // Page already loaded
      this.handleLoad();
    } else {
      // Wait for complete page load (all resources: images, CSS, JS)
      window.addEventListener("load", () => {
        console.log("HDT Loader: Page fully loaded - hiding now");
        this.handleLoad();
      });
    }

    // Smart fallback: Check if all critical resources are loaded
    this.setupSmartFallback();

    // Listen to Shopify theme editor events
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        this.hideLoader();
      });
    }
  }

  setupSmartFallback() {
    // Check images loading status periodically
    const checkImagesInterval = setInterval(() => {
      if (this.isLoaded) {
        clearInterval(checkImagesInterval);
        return;
      }

      // Get all images on page
      const images = Array.from(document.images);
      const totalImages = images.length;

      if (totalImages === 0) {
        // No images, safe to hide
        console.log("HDT Loader: No images detected - hiding loader");
        this.hideLoader();
        clearInterval(checkImagesInterval);
        return;
      }

      // Check if all images are loaded
      const loadedImages = images.filter(
        (img) => img.complete && img.naturalHeight > 0
      );
      const progress = (loadedImages.length / totalImages) * 100;

      console.log(
        `HDT Loader: Images ${
          loadedImages.length
        }/${totalImages} loaded (${progress.toFixed(0)}%)`
      );

      // If all images loaded, hide loader
      if (loadedImages.length === totalImages) {
        console.log("HDT Loader: All images loaded - hiding now");
        this.hideLoader();
        clearInterval(checkImagesInterval);
      }
    }, this.options.checkInterval);

    // Optional: Add performance observer for more accuracy
    if ("PerformanceObserver" in window) {
      try {
        const perfObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            // Check if all resources are loaded
            if (
              entry.entryType === "resource" &&
              document.readyState === "complete"
            ) {
              console.log(
                "HDT Loader: Performance observer detected complete load"
              );
            }
          }
        });
        perfObserver.observe({ entryTypes: ["resource", "navigation"] });
      } catch (e) {
        console.warn("HDT Loader: PerformanceObserver not supported", e);
      }
    }
  }

  handleLoad() {
    // Page is fully loaded - hide immediately
    console.log("HDT Loader: Page loaded, hiding now");
    this.hideLoader();
  }

  hideLoader() {
    if (this.isLoaded) return;

    this.isLoaded = true;

    // Add loaded class for CSS transition
    if (this.loader) {
      this.loader.classList.add("hdt-loaded");
    }

    // Remove loading class from body and html
    document.body.classList.remove("hdt-loading");
    document.documentElement.classList.remove("hdt-loading");

    // Trigger custom event
    document.dispatchEvent(new CustomEvent("hdtPageLoaded"));

    // Remove loader from DOM after transition
    setTimeout(() => {
      if (this.loader && this.loader.parentNode) {
        this.loader.parentNode.removeChild(this.loader);
      }
    }, this.options.fadeOutDuration);
  }

  // Public method to manually hide loader
  hide() {
    this.hideLoader();
  }

  // Public method to show loader again
  show() {
    if (this.loader) {
      this.loader.classList.remove("hdt-loaded");
      document.body.classList.add("hdt-loading");
      document.documentElement.classList.add("hdt-loading");
      this.isLoaded = false;
    }
  }
}

// Auto-initialize
function initHDTPageLoader() {
  // Get options from data attributes if available
  const loaderOptions = {
    logoAnimation: document.body.dataset.loaderAnimation || "pulse",
    loaderStyle: document.body.dataset.loaderStyle || "spinner",
    showLoader: document.body.dataset.loaderEnabled === "true",
  };

  // Check if loader should be shown
  if (!loaderOptions.showLoader) {
    const loader = document.querySelector(".hdt-page-loader");
    if (loader) {
      loader.style.display = "none";
    }
    return;
  }

  // Initialize loader
  window.hdtPageLoader = new HDTPageLoader(loaderOptions);

  // Expose class to global scope
  window.HDTPageLoader = HDTPageLoader;
}

// Run when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHDTPageLoader);
} else {
  initHDTPageLoader();
}

// Ensure loader instance exists
if (!window.hdtPageLoader) {
  console.warn("HDT Loader: Instance not found, creating fallback");
  // Fallback: Force hide if loader instance not found after page load
  window.addEventListener("load", function () {
    setTimeout(function () {
      const loader = document.querySelector(".hdt-page-loader");
      if (loader && !loader.classList.contains("hdt-loaded")) {
        console.log("HDT Loader: Fallback hide triggered");
        loader.classList.add("hdt-loaded");
        document.body.classList.remove("hdt-loading");
        document.documentElement.classList.remove("hdt-loading");
      }
    }, 500);
  });
}

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = HDTPageLoader;
}
